import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  FiPlus,
  FiTrash2,
  FiEdit,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import Spinner from "../../utils/Loader";
import UpdateBuyInrModal from "./UpdateBuyInrModal";

function AllBuyInr() {
  const [buyInrList, setBuyInrList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 10;

  // Modal States
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Delete Action Modal
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  const fetchBuyInr = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const response = await makeRequestAuth("get", "/admin/getAllBuyInr", {
        page,
        limit: pageSize,
      });

      // Based on your controller response structure
      const dataPayload = response?.data?.data || [];
      const pagination = response?.data?.pagination || {};

      setBuyInrList(dataPayload);
      setTotalRecords(pagination.total || 0);
      setTotalPages(pagination.totalPages || 1);
      setCurrentPage(pagination.page || 1);
    } catch (err) {
      showErrorToast(err.message || "Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBuyInr(1);
  }, [fetchBuyInr]);

  const openEditModal = (item) => {
    setSelectedRecord(item);
    setIsEditModalOpen(true);
  };

  const handleDelete = async () => {
    const { id } = deleteModal;
    try {
      await makeRequestAuth("post", "/admin/deleteBuyInr", { id });
      setBuyInrList((prev) => prev.filter((item) => item._id !== id));
      showSuccessToast("Record deleted successfully");

      // Optionally fetch again to correct pagination counts
      fetchBuyInr(currentPage);
    } catch (err) {
      showErrorToast("Failed to delete record");
    } finally {
      setDeleteModal({ open: false, id: null });
    }
  };

  // Status Badge Styling
  const getStatusStyle = (status) => {
    switch (status) {
      case "approve":
        return "bg-emerald-100 text-emerald-600 border-emerald-200";
      case "reject":
        return "bg-red-100 text-red-600 border-red-200";
      case "process":
        return "bg-blue-100 text-blue-600 border-blue-200";
      case "pending":
        return "bg-yellow-100 text-yellow-600 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200"; // Cancel, expired
    }
  };

  // Local Search Filter
  const filteredList = buyInrList.filter(
    (item) =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.upiId?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (isLoading && buyInrList.length === 0) return <Spinner />;

  return (
    <div className="grid grid-cols-1 gap-6 p-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white dark:bg-[#1E2738] text-gray-800 dark:text-white rounded-2xl p-6 md:p-8 border border-purple-300 dark:border-purple-600/50 shadow-xl">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Buy INR Requests
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Total {totalRecords} records found
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative w-full sm:w-56">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search name or UPI..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none transition"
              />
            </div>

            <Link to="/buy-inr/create">
              <button className="flex items-center justify-center gap-2 w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2 rounded-xl font-medium shadow-lg hover:scale-[1.02] transition">
                <FiPlus size={18} />
                <span>Add Record</span>
              </button>
            </Link>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-6">
          <div className="overflow-x-auto">
            <table className="w-full table-auto text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 text-sm uppercase tracking-wider font-semibold border-b border-gray-200 dark:border-gray-700">
                  <th className="py-4 px-6 text-center w-16">#</th>
                  <th className="py-4 px-6">Name</th>
                  <th className="py-4 px-6 text-center">Amount</th>
                  <th className="py-4 px-6 text-center">Percent</th>
                  <th className="py-4 px-6 text-center">UPI ID</th>
                  <th className="py-4 px-6 text-center">Status</th>
                  <th className="py-4 px-6 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredList.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-10 text-gray-500">
                      No Records Found
                    </td>
                  </tr>
                ) : (
                  filteredList.map((item, index) => (
                    <tr
                      key={item._id}
                      className="hover:bg-blue-50/50 dark:hover:bg-gray-700/30 transition"
                    >
                      <td className="py-4 px-6 text-center">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="py-4 px-6 font-medium">
                        {item.name || "-"}
                      </td>
                      <td className="py-4 px-6 text-center font-semibold">
                        ₹{item.amount || 0}
                      </td>
                      <td className="py-4 px-6 text-center">
                        {item.percentage || 0}%
                      </td>
                      <td className="py-4 px-6 text-center text-sm font-mono text-gray-600 dark:text-gray-400">
                        {item.upiId || "-"}
                      </td>

                      <td className="py-4 px-6 text-center">
                        <span
                          className={`text-xs font-bold px-3 py-1.5 rounded-full border uppercase tracking-wider ${getStatusStyle(item.status)}`}
                        >
                          {item.status || "PENDING"}
                        </span>
                      </td>

                      <td className="py-4 px-6 text-right">
                        <div className="flex justify-end items-center gap-3">
                          <button
                            onClick={() => openEditModal(item)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition dark:text-blue-400 dark:hover:bg-blue-900/30"
                            title="Edit"
                          >
                            <FiEdit size={18} />
                          </button>

                          <button
                            onClick={() =>
                              setDeleteModal({ open: true, id: item._id })
                            }
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition dark:text-red-400 dark:hover:bg-red-900/30"
                            title="Delete"
                          >
                            <FiTrash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PAGINATION CONTROLS */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center px-4">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => fetchBuyInr(currentPage - 1)}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition text-gray-700 dark:text-gray-300"
              >
                <FiChevronLeft size={20} />
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => fetchBuyInr(currentPage + 1)}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition text-gray-700 dark:text-gray-300"
              >
                <FiChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white dark:bg-[#1E2738] p-8 rounded-2xl shadow-2xl max-w-sm w-full text-center border border-gray-200 dark:border-gray-600 animate-fadeIn">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6 bg-red-100 text-red-600">
              <FiTrash2 size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Delete Record?
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-8">
              Are you sure you want to permanently delete this record?
            </p>

            <div className="flex justify-center gap-3">
              <button
                onClick={() => setDeleteModal({ open: false, id: null })}
                className="flex-1 px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium transition hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-5 py-2.5 rounded-xl text-white font-medium shadow-lg transition bg-red-600 hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      <UpdateBuyInrModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        fetchBuyInr={() => fetchBuyInr(currentPage)}
        initialData={selectedRecord}
      />
    </div>
  );
}

export default AllBuyInr;
